import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  subscribeVisitStartCheckIns,
  subscribeVisitStartConfig,
} from "services/visitStartCheckInsService";

export const VisitStartCheckInsContext = React.createContext({
  checkIns: [],
  checkInsLoaded: false,
  checkInsError: null,
  config: null,
  configLoaded: false,
  configError: null,
});

/**
 * VisitStart state for one facility — the check-in inbox and the clinic's own
 * question-set configuration — cached the same way Auth.js caches medications.
 *
 * Mounted once in DashboardRoutes, above the routes rather than inside the
 * Check-ins page itself, so navigating to another dashboard page and back does
 * not tear the subscriptions down and re-show a loading spinner for data that
 * has not changed. Both are live (onSnapshot) rather than fetched once: the
 * config can be written from two places — this page's own "turn on" button and
 * the Settings page's question-set editor — and a live subscription reflects
 * either without the writer having to remember to invalidate a cache.
 */
export const VisitStartCheckInsProvider = ({ clinicId, children }) => {
  const [checkIns, setCheckIns] = useState([]);
  const [checkInsLoaded, setCheckInsLoaded] = useState(false);
  const [checkInsError, setCheckInsError] = useState(null);

  const [config, setConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    if (!clinicId) {
      setCheckIns([]);
      setCheckInsLoaded(false);
      setCheckInsError(null);
      return undefined;
    }
    setCheckInsLoaded(false);
    const unsubscribe = subscribeVisitStartCheckIns(
      clinicId,
      (rows) => {
        setCheckIns(rows);
        setCheckInsError(null);
        setCheckInsLoaded(true);
      },
      (err) => {
        console.error("Error in VisitStart check-ins snapshot:", err);
        setCheckInsError(
          "Could not load your check-ins. Please refresh, or try again in a moment.",
        );
        setCheckInsLoaded(true);
      },
    );
    return () => unsubscribe();
  }, [clinicId]);

  useEffect(() => {
    if (!clinicId) {
      setConfig(null);
      setConfigLoaded(false);
      setConfigError(null);
      return undefined;
    }
    setConfigLoaded(false);
    const unsubscribe = subscribeVisitStartConfig(
      clinicId,
      (next) => {
        setConfig(next);
        setConfigError(null);
        setConfigLoaded(true);
      },
      (err) => {
        console.error("Error in VisitStart config snapshot:", err);
        // Treat an unreadable config as off. The alternative — assuming on —
        // offers a QR code we cannot promise leads anywhere.
        setConfig(null);
        setConfigError(err?.message || "Could not load check-in settings.");
        setConfigLoaded(true);
      },
    );
    return () => unsubscribe();
  }, [clinicId]);

  return (
    <VisitStartCheckInsContext.Provider
      value={{
        checkIns,
        checkInsLoaded,
        checkInsError,
        config,
        configLoaded,
        configError,
      }}
    >
      {children}
    </VisitStartCheckInsContext.Provider>
  );
};

VisitStartCheckInsProvider.propTypes = {
  clinicId: PropTypes.string,
  children: PropTypes.node,
};
